import React, { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function Checkpoint({ chapterId, sectionIdx, checkpoint }) {
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);

  if (!checkpoint) return null;

  const check = async (idx) => {
    if (result || checking) return;
    setSelected(idx);
    setChecking(true);
    try {
      const r = await api.post(`/checkpoint/check?chapter_id=${chapterId}&section_idx=${sectionIdx}&answer=${idx}`);
      setResult(r.data);
    } catch (e) { /* ignore */ }
    finally { setChecking(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8"
      data-testid="checkpoint-block"
    >
      <Card className="p-5 lg:p-6 border-2 border-primary/20 bg-primary/5">
        <div className="flex items-center gap-2 mb-3">
          <HelpCircle size={16} className="text-primary" />
          <div className="label-caps text-primary">Quick Check</div>
        </div>
        <div className="font-medium mb-4 text-base">{checkpoint.q}</div>
        <div className="grid gap-2">
          {checkpoint.options.map((opt, i) => {
            const isPicked = selected === i;
            const isCorrect = result && i === result.correct_index;
            const isWrong = result && isPicked && !result.correct;
            return (
              <button
                key={i}
                type="button"
                data-testid={`checkpoint-option-${i}`}
                onClick={() => check(i)}
                disabled={!!result}
                className={`text-left px-3 py-2 rounded-md border text-sm transition-all ${
                  isCorrect ? "border-primary bg-primary/10" :
                  isWrong ? "border-destructive bg-destructive/10" :
                  isPicked ? "border-primary bg-secondary" :
                  "border-border hover:border-primary/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-muted-foreground">{String.fromCharCode(65 + i)}</span>
                  <span className="flex-1">{opt}</span>
                  {isCorrect && <CheckCircle2 size={14} className="text-primary" />}
                  {isWrong && <XCircle size={14} className="text-destructive" />}
                </div>
              </button>
            );
          })}
        </div>
        {result && (
          <div className={`mt-3 text-sm ${result.correct ? "text-primary" : "text-muted-foreground"}`} data-testid="checkpoint-explain">
            {result.correct ? "Correct." : "Not quite."} {result.explain}
          </div>
        )}
      </Card>
    </motion.div>
  );
}
